import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Users, Landmark, HeartHandshake, ChevronRight } from "lucide-react";
import HeroContinuum from "@/components/HeroContinuum";
import { Overline, Section, TrustStrip, PrimaryCTA, SecondaryCTA, StatLine } from "@/components/Section";
import WaitlistForm from "@/components/WaitlistForm";
import ChoroplethMotif from "@/components/ChoroplethMotif";
import ContinuumBand from "@/components/ContinuumBand";
import CommunityQuestionsTicker from "@/components/CommunityQuestionsTicker";
import StatusChip from "@/components/StatusChip";

const DOMAINS = [
  {
    n: "01",
    status: "LIVE",
    title: "Housing, homelessness & basic needs",
    desc: "Mandated Housing Needs Assessments, homelessness system intelligence, non-market inventory. Live today — the module that builds your community's data foundation.",
    to: "/solutions/housing",
    label: "Explore housing",
  },
  {
    n: "02",
    status: "IN DEVELOPMENT",
    title: "Mental health, substance use & crisis",
    desc: "Where are crisis calls concentrating? Where are the service deserts? Community profiles and crisis-system dashboards for the FCSS desk that already owns the housing file.",
    to: "/solutions/mental-health",
    label: "See what's coming",
  },
  {
    n: "03",
    status: "IN DEVELOPMENT",
    title: "Disability, aging & continuing care",
    desc: "The aging population mapped against beds, home care, and waitlists — which communities hit demographic tipping points without corresponding service growth.",
    to: "/solutions/aging-disability",
    label: "See what's coming",
  },
  {
    n: "04",
    status: "IN DEVELOPMENT",
    title: "Health-care systems",
    desc: "Primary-care access, ER utilisation as an upstream-failure signal, service capacity against population change.",
    to: "/solutions/health-care",
    label: "See what's coming",
  },
];

const AUDIENCES = [
  {
    icon: HeartHandshake,
    title: "Small agencies & NFPs",
    pitch: "The evidence your funding application needs — lineage-backed, sized for a single application, at a price you can actually budget for.",
    to: "/solutions/housing#pricing",
  },
  {
    icon: Building2,
    title: "Municipalities",
    pitch: "A compliant HNA, a live evidence base, and named accountability on every statutory output. Built for the legislated cycle.",
    to: "/solutions/housing",
  },
  {
    icon: Users,
    title: "Funders & foundations",
    pitch: "Answer the disparity question publicly: does our money land where need is? The disparity layer is the product.",
    to: "/platform#service-landscape",
  },
  {
    icon: Landmark,
    title: "Government",
    pitch: "Standardisation, comparability, auditability. Versioned methodology, Canadian residency, transparent lineage.",
    to: "/method",
  },
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section data-testid="home-hero" className="relative bg-[#0A1224] text-white overflow-hidden">
        <ContinuumBand className="absolute top-0 left-0 right-0 h-1" testid="hero-top-band" />
        <div className="absolute inset-0 bg-grain opacity-50 pointer-events-none" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-24 md:pt-32 md:pb-32 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 reveal">
              <Overline className="text-slate-400 mb-8" testid="hero-overline">
                Continuum · Evidence infrastructure for the social sector
              </Overline>
              <h1 className="text-5xl md:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.92]">
                The evidence base <br className="hidden md:block" />
                <span className="text-[#FF8A00]">the social sector</span> runs on.
              </h1>
              <p className="text-lg md:text-2xl text-slate-300 mt-10 max-w-3xl leading-relaxed">
                One place-based evidence platform spanning the full continuum of care —
                housing, mental health, aging and disability, health care. Persistent,
                transparent, affordable. Your community's data layer, owned by your community.
              </p>
              <div className="flex flex-wrap gap-3 mt-10">
                <PrimaryCTA to="/contact" testid="hero-walkthrough-cta">Book a walkthrough</PrimaryCTA>
                <SecondaryCTA to="/platform" testid="hero-platform-cta" dark>Explore the platform</SecondaryCTA>
              </div>
            </div>
            <div className="lg:col-span-4 reveal reveal-d2">
              <div className="bg-white p-8 border-t-2 border-[#FF8A00]">
                <Overline className="mb-3">Now live · Housing Needs Assessments</Overline>
                <p className="text-slate-600 text-sm mb-5 leading-relaxed">
                  The HNA module is live with Canadian municipal tenants. Join the list to get
                  module releases, methodology drops, and pricing decisions for the next three
                  domains as they're made.
                </p>
                <WaitlistForm compact testid="hero-waitlist-form" />
              </div>
            </div>
          </div>

          <div className="mt-20 reveal reveal-d3">
            <HeroContinuum />
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <Section className="!py-16" testid="home-trust-strip">
        <TrustStrip />
      </Section>

      {/* FOUR DOMAINS */}
      <Section testid="home-domains" className="!pt-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
          <div className="lg:col-span-7">
            <Overline className="mb-6">Four systems of care</Overline>
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter leading-tight">
              One platform. Four systems of care. <span className="text-[#FF8A00]">One evidence base.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-slate-600 leading-relaxed text-lg">
              Every Canadian community faces the same failure across its social systems:
              fragmented data, stale evidence, no way to see whether plans are working.
              Continuum builds one persistent evidence layer and points it at four systems in
              turn — starting where the mandate is.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
          {DOMAINS.map((d, i) => (
            <Link
              key={d.title}
              to={d.to}
              data-testid={`domain-card-${i}`}
              className="group bg-white p-8 hover:bg-[#0A1224] transition-colors duration-300 flex flex-col"
            >
              <div className="flex items-center justify-between mb-8">
                <StatusChip status={d.status} />
                <span className="text-xs font-black tabular-nums text-slate-400 group-hover:text-white/60">{d.n}</span>
              </div>
              <h3 className="text-xl md:text-2xl font-black tracking-tight text-[#0A1224] group-hover:text-white leading-tight">
                {d.title}
              </h3>
              <p className="text-slate-600 group-hover:text-slate-300 mt-4 leading-relaxed text-sm flex-1">{d.desc}</p>
              <div className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#0A1224] group-hover:text-[#FF8A00]">
                {d.label} <ArrowUpRight size={16} />
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* RED DEER QUESTION INTERRUPT */}
      <section data-testid="home-red-deer" className="bg-[#050A15] text-white py-24 md:py-32 relative overflow-hidden">
        <ContinuumBand className="absolute top-0 left-0 right-0 h-1" testid="red-deer-band" />
        <div className="absolute inset-0 bg-grain opacity-40 pointer-events-none" aria-hidden />
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center relative">
          <Overline className="text-slate-400 mb-8" testid="red-deer-overline">The question we keep getting asked</Overline>
          <p className="text-3xl md:text-5xl font-black tracking-tighter leading-[1.05] mb-10">
            "What agencies are operating in your community —{" "}
            <span className="text-[#FF8A00]">doing what, funded by whom?</span>"
          </p>
          <p className="text-slate-300 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Most councils can't answer that today. Continuum makes it a five-minute question.
            Across housing, mental health, seniors' care, and primary care — on one map.
          </p>
          <div className="mt-12 flex flex-wrap gap-3 justify-center">
            <PrimaryCTA to="/platform#service-landscape" testid="red-deer-cta">See how it works</PrimaryCTA>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <Section testid="home-audiences">
        <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
          <div className="lg:col-span-7">
            <Overline className="mb-6">Who it's for</Overline>
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter leading-tight">
              One platform. <span className="text-[#FF8A00]">Every actor</span> along the continuum.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-slate-600 leading-relaxed text-lg">
              We don't price the small agency out and we don't dumb the platform down for them.
              Same evidence base, different lenses.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
          {AUDIENCES.map((a, i) => {
            const Icon = a.icon;
            return (
              <Link key={a.title} to={a.to} data-testid={`audience-card-${i}`} className="bg-[#F8F9FA] hover:bg-white transition-colors p-8 group flex flex-col">
                <Icon className="text-[#0A1224] group-hover:text-[#FF8A00] transition-colors" size={28} />
                <h3 className="text-xl font-black tracking-tight text-[#0A1224] mt-6">{a.title}</h3>
                <p className="text-slate-600 mt-3 leading-relaxed flex-1">{a.pitch}</p>
                <div className="mt-6 inline-flex items-center gap-1 text-sm font-bold text-[#0A1224] group-hover:text-[#FF8A00]">
                  Learn more <ChevronRight size={16} />
                </div>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* WHAT COMMUNITIES ARE ASKING — anonymised, real submissions */}
      <CommunityQuestionsTicker />

      {/* THE COMMUNITY QUESTION + MOTIF */}
      <Section testid="home-motif-block" className="bg-[#F8F9FA]">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Overline className="mb-6">Built on public data + yours</Overline>
            <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-tight">
              Lineage you can defend in a procurement review.
            </h2>
            <p className="text-slate-600 leading-relaxed text-lg mt-6">
              Every indicator on Continuum carries its source, its method version, and the date
              it was last refreshed. Your data layer stays yours — versioned, exportable, and
              never used to train anything.
            </p>
            <div className="mt-8">
              <SecondaryCTA to="/method" testid="motif-method-cta">Read the method</SecondaryCTA>
            </div>
          </div>
          <div className="lg:col-span-6">
            <ChoroplethMotif className="w-full h-auto border border-slate-200" />
          </div>
        </div>
      </Section>

      {/* PROOF NUMBERS — only honest ones */}
      <Section testid="home-stats">
        <Overline className="mb-6">What's true today</Overline>
        <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-tight max-w-3xl mb-12">
          No fabricated metrics. No invented case studies. Here's the state of the build.
        </h2>
        <StatLine items={[
          { label: "Live modules", value: "4", note: "HNA · Benchmark · Snapshot · Council Pack" },
          { label: "Methodology", value: "Versioned", note: "Federal HICC template & BC HNR, from one engine" },
          { label: "Data residency", value: "Canadian", note: "Hosted in Canada, exportable" },
          { label: "Sign-off model", value: "Named", note: "On every published output" },
        ]} />
      </Section>

      {/* FINAL CTA */}
      <section data-testid="home-final-cta" className="bg-[#0A1224] text-white py-24 md:py-32 relative">
        <ContinuumBand className="absolute top-0 left-0 right-0 h-1" testid="final-cta-band" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <Overline className="text-slate-400 mb-6">Talk to us</Overline>
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-tight mb-8">
            Ask your community question.
          </h2>
          <p className="text-slate-300 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
            Tell us one question you'd want Continuum to answer for your community. We'll
            respond from a human inbox within two business days.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <PrimaryCTA to="/contact" testid="final-walkthrough-cta">Book a walkthrough</PrimaryCTA>
            <SecondaryCTA to="/why" testid="final-why-cta" dark>Read why we're building this</SecondaryCTA>
          </div>
        </div>
      </section>
    </>
  );
}

// end of file
